import React, { useState, useMemo, useEffect } from 'react';
import Modal from '@components/ui/general/Modal';
import UnitsPreview from '@components/ui/general/UnitsPreview';
import { Trash2, Play, Save, Loader2, Pencil, Check } from 'lucide-react';
import { useT } from '@/i18n';
import './CustomTemplatesModal.css';

/**
 * Compare two unit arrays to check if they're equivalent
 * @param {Array} unitsA - [{unit_template_slug, quantity}]
 * @param {Array} unitsB - [{unit_template_slug, quantity}]
 * @returns {boolean}
 */
function areUnitsEqual(unitsA, unitsB) {
  if (!unitsA || !unitsB) return false;
  if (unitsA.length !== unitsB.length) return false;

  // Create maps for comparison
  const mapA = {};
  unitsA.forEach(u => { mapA[u.unit_template_slug] = u.quantity; });

  const mapB = {};
  unitsB.forEach(u => { mapB[u.unit_template_slug] = u.quantity; });

  // Check all keys match
  const keysA = Object.keys(mapA);
  const keysB = Object.keys(mapB);
  if (keysA.length !== keysB.length) return false;

  return keysA.every(key => mapA[key] === mapB[key]);
}

/**
 * Modal para gestionar plantillas custom del jugador
 * Permite: ver lista, renombrar, eliminar, aplicar, guardar roster actual
 */
export default function CustomTemplatesModal({
  isOpen,
  onClose,
  playerTemplates = [],
  purchasedUnits = [],
  onApplyTemplate,
  onCreateTemplate,
  onUpdateTemplate,
  onDeleteTemplate,
  isCreating = false,
  isUpdating = false,
  isDeleting = false,
  maxTemplates, // Required - no fallback
}) {
  const t = useT();

  // Estado para edición inline de nombres
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  // Estado para nombre de nueva plantilla
  const [newTemplateName, setNewTemplateName] = useState('');

  // Convertir purchasedUnits a formato de units para guardar
  const currentRosterAsUnits = useMemo(() => {
    if (!purchasedUnits || purchasedUnits.length === 0) return [];

    // Agrupar por template slug
    const grouped = {};
    purchasedUnits.forEach(pu => {
      // Use the actual slug from unit_template, fallback to generating from name
      const unitTemplate = pu.unit?.unit_template;
      const slug = unitTemplate?.slug || unitTemplate?.name?.toLowerCase().replace(/\s+/g, '-');
      if (slug) {
        if (!grouped[slug]) {
          grouped[slug] = { unit_template_slug: slug, quantity: 0 };
        }
        grouped[slug].quantity += 1;
      }
    });

    return Object.values(grouped);
  }, [purchasedUnits]);

  // Sort templates by created_at descending (newest first)
  const sortedTemplates = useMemo(() => {
    return [...playerTemplates].sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA; // Descending
    });
  }, [playerTemplates]);

  // Check if current roster matches any saved template
  const matchingTemplate = useMemo(() => {
    if (!currentRosterAsUnits.length) return null;
    return sortedTemplates.find(t => areUnitsEqual(t.units, currentRosterAsUnits));
  }, [currentRosterAsUnits, sortedTemplates]);

  const canCreateMore = playerTemplates.length < maxTemplates;
  const hasCurrentRoster = currentRosterAsUnits.length > 0;
  const isAlreadySaved = !!matchingTemplate;

  // Inicializar nombre sugerido solo cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      setNewTemplateName(t('templates.my_template', { num: playerTemplates.length + 1 }));
    }
  }, [isOpen, playerTemplates.length]);

  // Handlers para edición de plantillas existentes
  const handleStartEdit = (template) => {
    setEditingId(template.id);
    setEditName(template.name);
  };

  const handleSaveEdit = (templateId) => {
    if (editName.trim() && editName !== playerTemplates.find(t => t.id === templateId)?.name) {
      onUpdateTemplate?.(templateId, { name: editName.trim() });
    }
    setEditingId(null);
    setEditName('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleKeyDown = (e, templateId) => {
    if (e.key === 'Enter') {
      handleSaveEdit(templateId);
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleDelete = (templateId) => {
    if (window.confirm(t('templates.confirm_delete'))) {
      onDeleteTemplate?.(templateId);
    }
  };

  const handleApply = (templateId) => {
    onApplyTemplate?.(templateId, 'custom');
    onClose();
  };

  // Handler para guardar nueva plantilla
  const handleSaveNewTemplate = () => {
    if (newTemplateName.trim() && hasCurrentRoster && canCreateMore) {
      onCreateTemplate?.({
        name: newTemplateName.trim(),
        units: currentRosterAsUnits,
      });
      // Reset nombre para la siguiente
      setNewTemplateName(t('templates.my_template', { num: playerTemplates.length + 2 }));
    }
  };

  const handleNewTemplateKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveNewTemplate();
    }
  };

  const isLoading = isCreating || isUpdating || isDeleting;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <>
          {t('templates.my_templates')}
          <span className="modal-title-counter">{playerTemplates.length}/{maxTemplates}</span>
        </>
      }
      className="custom-templates-modal"
    >
      <div className="custom-templates-content">
        {/* ROSTER ACTUAL - Siempre arriba */}
        <div className="current-roster-section">
          <h3 className="section-title">Roster Actual</h3>

          {hasCurrentRoster ? (
            <div className={`custom-template-item current-roster-card ${isAlreadySaved ? 'current-roster-card--saved' : ''}`}>
              {/* Header: input + save button OR saved message */}
              <div className="template-header">
                {isAlreadySaved ? (
                  // Already saved - show confirmation message
                  <div className="template-saved-message">
                    <Check size={14} className="template-saved-icon" />
                    <span>Guardada como: <strong>{matchingTemplate.name}</strong></span>
                  </div>
                ) : (
                  // Not saved - show input + save button
                  <>
                    <div className="template-name-wrapper">
                      <Pencil size={14} className="template-edit-icon" />
                      <input
                        type="text"
                        className="template-name-input"
                        value={newTemplateName}
                        onChange={(e) => setNewTemplateName(e.target.value)}
                        onKeyDown={handleNewTemplateKeyDown}
                        placeholder="Nombre de la plantilla..."
                        disabled={isCreating || !canCreateMore}
                      />
                    </div>
                    <button
                      type="button"
                      className="button-mini button-mini-cta"
                      onClick={handleSaveNewTemplate}
                      disabled={isCreating || !newTemplateName.trim() || !canCreateMore}
                      title={!canCreateMore ? `Límite de ${maxTemplates} plantillas` : 'Guardar plantilla'}
                    >
                      {isCreating ? <Loader2 size={14} className="spinner" /> : <Save size={14} />}
                    </button>
                  </>
                )}
              </div>
              {/* Preview de unidades */}
              <div className="template-units">
                <UnitsPreview units={currentRosterAsUnits} />
              </div>
            </div>
          ) : (
            <div className="current-roster-empty">
              Sin unidades en tu roster actual
            </div>
          )}

          {!canCreateMore && hasCurrentRoster && !isAlreadySaved && (
            <div className="limit-message">
              Límite de {maxTemplates} plantillas alcanzado
            </div>
          )}
        </div>

        {/* PLANTILLAS GUARDADAS */}
        <div className="saved-templates-section">
          <h3 className="section-title">Plantillas Guardadas</h3>

          <div className="custom-templates-list">
            {sortedTemplates.length === 0 ? (
              <div className="templates-empty">
                No tienes plantillas guardadas
              </div>
            ) : (
              sortedTemplates.map((template) => (
                <div key={template.id} className="custom-template-item">
                  {/* Header row: pencil + name + actions */}
                  <div className="template-header">
                    {editingId === template.id ? (
                      <input
                        type="text"
                        className="template-name-input"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onBlur={() => handleSaveEdit(template.id)}
                        onKeyDown={(e) => handleKeyDown(e, template.id)}
                        autoFocus
                        disabled={isLoading}
                      />
                    ) : (
                      <button
                        type="button"
                        className="template-name"
                        onClick={() => handleStartEdit(template)}
                        disabled={isLoading}
                        title="Click para editar nombre"
                      >
                        <Pencil size={14} className="template-edit-icon" />
                        {template.name}
                      </button>
                    )}

                    {/* Acciones */}
                    <div className="template-actions">
                      <button
                        type="button"
                        className="button-mini"
                        onClick={() => handleApply(template.id)}
                        disabled={isLoading}
                        title="Aplicar plantilla"
                      >
                        <Play size={14} />
                      </button>
                      <button
                        type="button"
                        className="button-mini button-mini-danger"
                        onClick={() => handleDelete(template.id)}
                        disabled={isLoading}
                        title="Eliminar plantilla"
                      >
                        {isDeleting ? <Loader2 size={14} className="spinner" /> : <Trash2 size={14} />}
                      </button>
                    </div>
                  </div>

                  {/* Preview de unidades */}
                  <div className="template-units">
                    <UnitsPreview units={template.units} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
